import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Briefcase, MapPin, Calendar, ExternalLink, Pencil, Building2, Banknote, ArrowRight, Upload, Bot } from "lucide-react";
import JobOpportunityClient, { JobOpportunity } from "../data/api/JobOpportunityClient";
import JobOpportunityFormModal from "../components/JobOpportunityFormModal";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";
import "../JobOpportunity.css";

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case "Open":
      return "jo-badge-green";
    case "Closed":
      return "jo-badge-gray";
    case "Draft":
      return "jo-badge-amber";
    default:
      return "jo-badge-gray";
  }
};

const getStatusColorVars = (status?: string) => {
  switch (status) {
    case "Open":
      return { "--card-color": "#059669", "--card-color-rgb": "5, 150, 105" } as React.CSSProperties;
    case "Closed":
      return { "--card-color": "#64748b", "--card-color-rgb": "100, 116, 139" } as React.CSSProperties;
    case "Draft":
      return { "--card-color": "#d97706", "--card-color-rgb": "217, 119, 6" } as React.CSSProperties;
    default:
      return { "--card-color": "#d97706", "--card-color-rgb": "217, 119, 6" } as React.CSSProperties;
  }
};

const getStatusLabel = (status?: string) => status || "Draft";

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const JobOpportunitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<JobOpportunity[]>([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAddActions, setShowAddActions] = useState(false);
  const addActionsRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addActionsRef.current && !addActionsRef.current.contains(event.target as Node)) {
        setShowAddActions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await JobOpportunityClient.listOpportunities({
        page_number: 1,
        limit: 100,
      });
      setOpportunities(data.records || []);
      setTotal(data.total_no_of_records || 0);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || "Failed to load job opportunities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  const filtered = opportunities.filter((o) =>
    o.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateSuccess = () => {
    setIsModalOpen(false);
    fetchOpportunities();
  };

  const handleEdit = (o: JobOpportunity) => {
    navigate(`/dashboard/job-opportunity/edit/${o.opportunity_id}`);
  };

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader isLoading={loading} message="Loading job opportunities..." />

      <Alert
        show={!!error}
        type="error"
        title="Error"
        message={error || ""}
        primaryButtonText="Close"
        onPrimaryClick={() => setError(null)}
        onClose={() => setError(null)}
      />

      <div className="lp-hero" style={{ paddingBottom: "10px" }}>
        <div className="lp-hero-left">
          <h1>Job Opportunities</h1>
          <span className="lp-hero-chip">Manage all open &amp; closed job opportunities</span>
        </div>
        <div className="lp-hero-right" style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div style={{ position: 'relative' }} ref={addActionsRef}>
            <button
              className="btn-primary"
              onClick={() => setShowAddActions(p => !p)}
              style={{ borderRadius: "24px", height: 40, padding: "0 20px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 600 }}
            >
              <Plus size={16} /> Create Job Opportunity
            </button>
            {showAddActions && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                  background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.1), 0 4px 12px rgba(13,110,91,0.12)',
                  minWidth: '200px', zIndex: 20, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '6px',
                }}
              >
                <button
                  onClick={() => { setShowAddActions(false); navigate("/dashboard/job-opportunity/upload"); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px',
                    background: 'transparent', border: 'none', borderRadius: '10px',
                    fontSize: '13px', fontWeight: 500, color: 'var(--primary)', cursor: 'pointer', textAlign: 'left',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(13, 110, 91, 0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ width: 30, height: 30, borderRadius: '8px', background: 'linear-gradient(135deg, rgba(13, 110, 91, 0.1), rgba(13, 110, 91, 0.2))', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Upload size={15} color="var(--primary)" />
                  </span>
                  <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '13.5px' }}>Upload a JD</span>
                </button>
                <button
                  onClick={() => { setShowAddActions(false); navigate("/dashboard/job-opportunity/new"); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px',
                    background: 'transparent', border: 'none', borderRadius: '10px',
                    fontSize: '13px', fontWeight: 500, color: 'var(--primary)', cursor: 'pointer', textAlign: 'left',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(13, 110, 91, 0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ width: 30, height: 30, borderRadius: '8px', background: 'linear-gradient(135deg, rgba(13, 110, 91, 0.1), rgba(13, 110, 91, 0.2))', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Pencil size={15} color="var(--primary)" />
                  </span>
                  <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '13.5px' }}>Create manually</span>
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <div className="lp-table-panel" style={{ background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
        <div
          className="lp-table-panel-head"
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
            background: "#f8fafc",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            borderTopLeftRadius: "14px",
            borderTopRightRadius: "14px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "8px 16px",
                gap: "12px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: "rgba(13, 110, 91, 0.1)",
                  color: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Briefcase size={16} strokeWidth={2.5} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ fontSize: "1rem", color: "#64748b", fontWeight: 600, textTransform: "none", letterSpacing: "0.5px" }}>
                  Total Opportunities:
                </div>
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1,
                    letterSpacing: "-0.5px",
                  }}
                >
                  {total}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "24px",
                padding: "6px 16px",
                minWidth: "280px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                transition: "all 0.2s ease"
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = "var(--primary)"}
              onBlur={(e) => e.currentTarget.style.borderColor = "#e2e8f0"}
            >
              <Search size={16} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: "none",
                  background: "transparent",
                  outline: "none",
                  padding: "4px 12px",
                  fontSize: "0.9rem",
                  width: "100%",
                  color: "#0f172a"
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    color: "#94a3b8",
                    borderRadius: "50%"
                  }}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="jo-error-banner" style={{ margin: "0 24px 16px 24px" }}>
            {error}
          </div>
        )}

        <div className="lp-scroll-container">
          {!loading && filtered.length === 0 ? (
            <div className="jo-empty" style={{ margin: "24px" }}>
              <div className="jo-empty-icon">
                <Briefcase size={24} color="var(--primary)" />
              </div>
              <span>{searchTerm ? "No opportunities found matching criteria." : "No job opportunities yet. Create your first one!"}</span>
            </div>
          ) : (
            <div className="jo-grid">
              {filtered.map((o) => (
                <div key={o.opportunity_id} className="jo-card" style={getStatusColorVars(o.status)} onClick={() => navigate(`/dashboard/job-applications/${o.opportunity_id}`)}>

                  <div className="jo-card-header">
                    <div>
                      <h3 className="jo-card-title">{o.title}</h3>
                      <div className="jo-card-subtitle" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Building2 size={14} />
                          {o.for_organization_name || o.company_name || "Internal"}
                        </span>
                        {o.creation_method === "document_upload" && (
                          <span className="jo-badge" style={{ padding: "2px 6px", fontSize: "0.65rem", borderRadius: "6px", background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6", display: "flex", alignItems: "center", gap: "3px" }}>
                            <Bot size={10} /> AI Extracted
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <span className={`jo-badge ${statusBadgeClass(o.status)}`} style={{ padding: "4px 10px", fontSize: "0.75rem", borderRadius: "10px" }}>
                        {getStatusLabel(o.status)}
                      </span>
                    </div>
                  </div>

                  <div className="jo-card-body">
                    <div className="jo-card-detail">
                      <div className="jo-card-detail-icon"><MapPin size={15} /></div>
                      <span>{o.work_location || "—"}</span>
                    </div>
                    <div className="jo-card-detail">
                      <div className="jo-card-detail-icon"><Calendar size={15} /></div>
                      <span>Deadline: {formatDate(o.application_deadline) || "—"}</span>
                    </div>
                    {o.department && (
                      <div className="jo-card-detail">
                        <div className="jo-card-detail-icon"><Briefcase size={15} /></div>
                        <span>{o.department}</span>
                      </div>
                    )}
                  </div>

                  <div className="jo-card-footer">
                    <div className="jo-card-type">
                      {o.hiring_type} · {o.job_type}
                    </div>
                    <div className="jo-card-view-btn">
                      View Job <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <JobOpportunityFormModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

    </motion.div>
  );
};

export default JobOpportunitiesPage;
